import {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  buildBrand,
  alpha,
  formatDate,
  getGrade,
  getGradeColor,
  extractSeverity,
  cleanReportDetails,
  extractTitle,
  compactMeta,
  markdownToBlocks,
  slugify,
} from '../../pentest/reports/externalReportPdfHelpers';

export {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  alpha,
};

export const METHOD_ORDER = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

export const WEB_METHODOLOGY_PARAGRAPHS = [
  'We begin each web application assessment by confirming the authorized scope, understanding the application architecture, and identifying the workflows that matter most from both a business and security perspective. The assessment team reviews the exposed functionality, authentication flows, user roles, and input surfaces so we can build an accurate picture of how the application is intended to behave before testing begins.',
  'Once the application surface is mapped, we enumerate reachable pages, routes, parameters, APIs, and supporting components to understand how data moves through the platform. We inspect session handling, role boundaries, user-controlled input, file handling, business logic paths, and client-server interactions to identify where trust assumptions may break down. This phase also helps us fingerprint the observable technology stack and supporting integrations that influence likely attack paths.',
  'With that context established, we perform targeted validation against the application layer. The assessment team tests for issues such as broken access control, authentication weaknesses, injection opportunities, insecure direct object references, cross-site scripting, server-side request forgery, logic abuse, and other flaws that could affect confidentiality, integrity, or availability. Testing is carried out deliberately so findings reflect validated risk and reproducible impact rather than theoretical concerns alone.',
  'Throughout the engagement, we capture evidence, affected locations, and the technical details needed to reproduce each issue. We then translate those results into a report that explains what was identified, how it was verified, what the likely business impact is, and which remediation steps should be prioritized first. The goal is to give both leadership and technical teams a practical, defensible path from assessment through remediation.',
];

const gradeMessages = {
  'A+': 'Outstanding security posture. No material risk identified.',
  'A-': 'Strong posture with minor opportunities for hardening.',
  B: 'Good overall, address medium and low issues to further improve resilience.',
  C: 'Fair posture. Multiple findings should be prioritized for remediation.',
  D: 'Weak posture. Significant vulnerabilities require attention.',
  F: 'Critical risk. Immediate remediation is recommended.',
};

export const buildWebReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  stats,
  exploits,
  executiveSummary,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');

  const cleanedFindings = (exploits || [])
    .filter((item) => item && (item.name || item.report_details))
    .map((item, index) => {
      const cleanedDetails = cleanReportDetails(item.report_details || '');
      const severity = extractSeverity(item);
      const title = extractTitle(cleanedDetails || item.report_details || '', item.name || `Finding ${index + 1}`);

      return {
        ...item,
        severity,
        cleanedDetails,
        title,
        asset: compactMeta(item.impacted_assets || item.endpoint || item.host || item.asset || ''),
        id: `${slugify(title || item.name || `finding-${index + 1}`)}-${index + 1}`,
        markdownBlocks: markdownToBlocks(cleanedDetails || 'No additional details provided.'),
      };
    })
    .sort((left, right) => (SEVERITY_RANK[left.severity] ?? 999) - (SEVERITY_RANK[right.severity] ?? 999));

  const totalEndpoints = stats?.total_endpoints ?? stats?.counts?.endpoints ?? 0;
  const totalFindings = cleanedFindings.length;
  const riskScore = stats?.risk_score ?? stats?.score ?? null;
  const grade = getGrade(riskScore);
  const gradeColor = getGradeColor(grade);

  const severityCounts = SEVERITY_ORDER.reduce((accumulator, label) => ({
    ...accumulator,
    [label]: 0,
  }), {});

  cleanedFindings.forEach((item) => {
    severityCounts[item.severity] = (severityCounts[item.severity] || 0) + 1;
  });

  const highImpactCount = (severityCounts.Critical || 0) + (severityCounts.High || 0);
  const highImpactShare = totalFindings ? Math.round((highImpactCount / totalFindings) * 100) : 0;
  const maxSeverityCount = Math.max(1, ...SEVERITY_ORDER.map((label) => severityCounts[label] || 0));
  const severityStats = SEVERITY_ORDER.map((label) => {
    const value = severityCounts[label] || 0;
    return {
      label,
      value,
      color: SEVERITY_COLORS[label],
      share: totalFindings ? Math.round((value / totalFindings) * 100) : 0,
    };
  });

  const methodCounts = {
    GET: stats?.total_get_methods ?? 0,
    POST: stats?.total_post_methods ?? 0,
    PUT: stats?.total_put_methods ?? 0,
    DELETE: stats?.total_delete_methods ?? 0,
    PATCH: stats?.total_patch_methods ?? 0,
  };

  const totalMethodObservations = METHOD_ORDER.reduce((sum, label) => sum + (methodCounts[label] || 0), 0);
  const maxMethodCount = Math.max(1, ...METHOD_ORDER.map((label) => methodCounts[label] || 0));
  const methodStats = METHOD_ORDER.map((label) => {
    const value = methodCounts[label] || 0;
    return {
      label,
      value,
      share: totalMethodObservations ? Math.round((value / totalMethodObservations) * 100) : 0,
    };
  });

  const activeMethodCount = methodStats.filter((item) => item.value > 0).length;

  const topFindings = [...cleanedFindings]
    .map((item, index) => ({
      id: item.id || `finding-${index + 1}`,
      title: item.title,
      severity: item.severity,
      asset: item.asset,
    }))
    .sort((left, right) => (SEVERITY_SCORE[right.severity] || 0) - (SEVERITY_SCORE[left.severity] || 0))
    .slice(0, 5);

  const summaryParagraphs = String(executiveSummary || '')
    .split(/\n{2,}/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-security-assessment', title: 'Executive Security Assessment', level: 1 },
    { id: 'top-findings', title: 'Top Findings', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    { id: 'findings', title: 'Findings', level: 1 },
    ...cleanedFindings.map((item) => ({
      id: item.id,
      title: item.title,
      level: 2,
    })),
  ];

  return {
    pentestId,
    clientName: clientName || stats?.meta?.client_name || stats?.client || 'Client Name',
    companyName: companyName || 'StealthNet AI',
    reportDate: formatDate(stats?.meta?.generated_at || Date.now()),
    testingType: stats?.meta?.type || 'Web Application',
    organizationName: stats?.meta?.client_name || stats?.client || clientName || 'Client',
    brand,
    grade,
    gradeColor,
    gradeMessage: gradeMessages[grade] || '',
    riskScore,
    totalEndpoints,
    totalFindings,
    severityStats,
    maxSeverityCount,
    highImpactCount,
    highImpactShare,
    methodStats,
    maxMethodCount,
    totalMethodObservations,
    activeMethodCount,
    topFindings,
    executiveSummary: summaryParagraphs,
    methodology: WEB_METHODOLOGY_PARAGRAPHS,
    findings: cleanedFindings,
    tocItems,
  };
};
